import { useEffect } from 'react';
import { create } from 'zustand';
import { shouldSkipShortcut } from '@/utils/input';

export type SettingsModalSection =
  | 'profile'
  | 'general'
  | 'appearance'
  | 'accessibility'
  | 'ai'
  | 'privacy'
  | 'experimental'
  | 'about';

interface SettingsModalState {
  isOpen: boolean;
  initialSection: SettingsModalSection | null;
  open: (section?: SettingsModalSection) => void;
  close: () => void;
}

/**
 * Owns open/close state for the global Settings modal.
 *
 * The hook is intentionally minimal: it only exposes state and the
 * open/close actions. The Cmd/Ctrl+, shortcut binding lives in the
 * separate `useSettingsModalShortcut` hook so consumers that just want
 * to *read* state (or programmatically open the modal) don't attach a
 * redundant document keydown listener.
 *
 * Backed by a Zustand store so non-React callers — most notably the
 * retired /projects/X/settings and /apps/Y/settings routes that now
 * redirect here — can call `openSettingsModal(section)` /
 * `closeSettingsModal()` directly without a hook or context.
 */
export const useSettingsModalStore = create<SettingsModalState>((set) => ({
  isOpen: false,
  initialSection: null,
  open: (section) =>
    set({ isOpen: true, initialSection: section ?? null }),
  // Clear the requested section so a subsequent open() without an
  // argument restores the modal's last-selected section (managed inside
  // SettingsModal) rather than jumping back to the deep-link target.
  close: () => set({ isOpen: false, initialSection: null }),
}));

/** Open the Settings modal from anywhere (routes, command palette, etc.). */
export function openSettingsModal(section?: SettingsModalSection): void {
  useSettingsModalStore.getState().open(section);
}

/** Close the Settings modal from anywhere. */
export function closeSettingsModal(): void {
  useSettingsModalStore.getState().close();
}

export interface UseSettingsModalReturn {
  isOpen: boolean;
  initialSection: SettingsModalSection | null;
  open: (section?: SettingsModalSection) => void;
  close: () => void;
}

/**
 * Subscribe to the Settings modal state. Pass through the stable
 * open/close actions from the store so callers can wire onClick
 * handlers without an extra useCallback wrapper.
 */
export function useSettingsModal(): UseSettingsModalReturn {
  const isOpen = useSettingsModalStore((s) => s.isOpen);
  const initialSection = useSettingsModalStore((s) => s.initialSection);
  const open = useSettingsModalStore((s) => s.open);
  const close = useSettingsModalStore((s) => s.close);
  return { isOpen, initialSection, open, close };
}

/**
 * Binds the global `Cmd/Ctrl+,` chord to toggle the Settings modal.
 * Mount once (at RootLayout) — every additional caller would attach
 * another document keydown listener and double-toggle the modal.
 *
 * Skipped when focus is inside an editable surface so typing a comma
 * in an input never opens Settings.
 */
export function useSettingsModalShortcut(): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (shouldSkipShortcut(event)) return;
      const isComma = event.key === ',' || event.code === 'Comma';
      if (isComma && (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey) {
        event.preventDefault();
        if (event.repeat) return;
        const store = useSettingsModalStore.getState();
        if (store.isOpen) {
          store.close();
        } else {
          store.open();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
}
